import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import siteConfig from "../Datas/siteConfig";
import "../css/header.scss";
import { CartContext } from "./CartContext";

const primaryNavigation = [
  { label: "Women", to: "/shop?audience=women" },
  { label: "Men", to: "/shop?audience=men" },
  { label: "Kids", to: "/shop?audience=kids" },
  { label: "Footwear", to: "/shop?category=footwear" },
  { label: "Equipment", to: "/shop?category=equipment" },
  { label: "Our Story", to: "/about" },
  { label: "Shop All", to: "/shop", accent: true },
];

const utilityNavigation = [
  { label: "Delivery", to: "/shipping" },
  { label: "Returns", to: "/returns" },
  { label: "Help & contact", to: "/contact" },
];

function HeaderIcon({ name }) {
  const commonProps = {
    "aria-hidden": true,
    className: "site-header__icon",
    fill: "none",
    viewBox: "0 0 24 24",
  };

  if (name === "search") {
    return (
      <svg {...commonProps}>
        <circle cx="10.8" cy="10.8" r="6.8" />
        <path d="m16 16 4.1 4.1" />
      </svg>
    );
  }

  if (name === "heart") {
    return (
      <svg {...commonProps}>
        <path d="M20.7 5.8a5.3 5.3 0 0 0-7.5 0L12 7l-1.2-1.2a5.3 5.3 0 0 0-7.5 7.5L12 22l8.7-8.7a5.3 5.3 0 0 0 0-7.5Z" />
      </svg>
    );
  }

  if (name === "bag") {
    return (
      <svg {...commonProps}>
        <path d="M4.5 8.5h15l-1 12h-13l-1-12Z" />
        <path d="M8.5 9V7a3.5 3.5 0 0 1 7 0v2" />
      </svg>
    );
  }

  return (
    <svg {...commonProps}>
      <path d="M3 6.5h18M3 12h18M3 17.5h18" />
    </svg>
  );
}

function formatThreshold(value) {
  try {
    return new Intl.NumberFormat(siteConfig.locale || "en-SG", {
      style: "currency",
      currency: siteConfig.currency || "USD",
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${siteConfig.currency || "USD"} ${value}`;
  }
}

function getRegionLabel() {
  const locale = siteConfig.locale || "en-SG";
  const regionCode =
    siteConfig.countryCode || locale.split("-")[1]?.toUpperCase() || "SG";

  try {
    return (
      new Intl.DisplayNames([locale], { type: "region" }).of(regionCode) ||
      regionCode
    );
  } catch {
    return regionCode;
  }
}

function scrollToDocumentStart() {
  const reduceMotion = window.matchMedia?.(
    "(prefers-reduced-motion: reduce)"
  ).matches;
  window.scrollTo({ top: 0, behavior: reduceMotion ? "auto" : "smooth" });
}

function Header() {
  const cartState = useContext(CartContext) || {};
  const { cartItems = [], wishlistItems = [], cartCount } = cartState;
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const mobileToggleRef = useRef(null);
  const mobileNavigationRef = useRef(null);
  const searchActivatorRef = useRef(null);
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const regionLabel = getRegionLabel();

  const resolvedCartCount =
    typeof cartCount === "number"
      ? cartCount
      : cartItems.reduce(
          (total, item) => total + Math.max(0, Number(item.quantity) || 0),
          0
        );
  const wishlistCount = Array.isArray(wishlistItems) ? wishlistItems.length : 0;

  const serviceMessages = useMemo(() => {
    const configuredMessages = (siteConfig.services || [])
      .map((service) => service.title)
      .filter(Boolean)
      .slice(0, 3);

    if (configuredMessages.length === 3) return configuredMessages;

    return [
      `Tracked delivery ${formatThreshold(siteConfig.shippingCost || 9.99)}`,
      "Free returns within 30 days",
      "Extended product warranty",
    ];
  }, []);

  useEffect(() => {
    setIsMobileOpen(false);
    setIsSearchOpen(false);
  }, [pathname, search]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key !== "Escape") return;

      if (isSearchOpen) {
        setIsSearchOpen(false);
        window.requestAnimationFrame(() => searchActivatorRef.current?.focus());
        return;
      }

      if (isMobileOpen) {
        setIsMobileOpen(false);
        window.requestAnimationFrame(() => mobileToggleRef.current?.focus());
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isMobileOpen, isSearchOpen]);

  useEffect(() => {
    if (!isMobileOpen) return undefined;

    const animationFrame = window.requestAnimationFrame(() => {
      mobileNavigationRef.current?.querySelector("a")?.focus();
    });

    return () => window.cancelAnimationFrame(animationFrame);
  }, [isMobileOpen]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    if (isMobileOpen) document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isMobileOpen]);

  const handleNavigation = () => {
    setIsMobileOpen(false);
    setIsSearchOpen(false);
    scrollToDocumentStart();
  };

  const toggleSearch = (event) => {
    if (!isSearchOpen) searchActivatorRef.current = event.currentTarget;
    setIsSearchOpen((isOpen) => !isOpen);
    setIsMobileOpen(false);
  };

  const closeSearch = () => {
    setIsSearchOpen(false);
    window.requestAnimationFrame(() => searchActivatorRef.current?.focus());
  };

  const toggleMobileMenu = () => {
    setIsMobileOpen((isOpen) => !isOpen);
    setIsSearchOpen(false);
  };

  const handleSearch = (event) => {
    event.preventDefault();
    const query = searchTerm.trim();
    navigate(query ? `/shop?search=${encodeURIComponent(query)}` : "/shop");
    setIsSearchOpen(false);
    setIsMobileOpen(false);
    scrollToDocumentStart();
  };

  return (
    <header className="site-header">
      <div className="site-header__service-strip">
        <div
          className="site-header__service-inner"
          aria-label="Shopping benefits"
        >
          {serviceMessages.map((message) => (
            <span className="site-header__service-message" key={message}>
              {message}
            </span>
          ))}
        </div>
      </div>

      <div className="site-header__utility-bar">
        <div className="site-header__utility-inner">
          <span className="site-header__tagline">
            {siteConfig.tagline || siteConfig.description}
          </span>
          <div className="site-header__utility-links">
            <Link to="/about" onClick={handleNavigation}>
              Our story
            </Link>
            <Link to="/contact" onClick={handleNavigation}>
              Help
            </Link>
            <span className="site-header__region" aria-label="Store region">
              {regionLabel} · {siteConfig.currency || "USD"}
            </span>
          </div>
        </div>
      </div>

      <div className="site-header__main">
        <button
          type="button"
          ref={mobileToggleRef}
          className="site-header__mobile-toggle"
          aria-controls="mobile-navigation"
          aria-expanded={isMobileOpen}
          aria-label={isMobileOpen ? "Close navigation" : "Open navigation"}
          onClick={toggleMobileMenu}
        >
          {isMobileOpen ? (
            <span className="site-header__close" aria-hidden="true" />
          ) : (
            <HeaderIcon name="menu" />
          )}
        </button>

        <button
          type="button"
          className="site-header__search-trigger site-header__search-trigger--desktop"
          aria-controls="site-search"
          aria-expanded={isSearchOpen}
          onClick={toggleSearch}
        >
          <HeaderIcon name="search" />
          <span>Search</span>
        </button>

        <Link
          className="site-header__brand"
          to="/"
          onClick={handleNavigation}
          aria-label={`${siteConfig.brandName} home`}
        >
          {siteConfig.logo && (
            <img src={siteConfig.logo} alt="" aria-hidden="true" />
          )}
          <span className="site-header__brand-copy">
            <span className="site-header__brand-name">
              {siteConfig.brandName}
            </span>
            <span className="site-header__brand-tag">Outdoor equipment</span>
          </span>
        </Link>

        <div className="site-header__actions">
          <button
            type="button"
            className="site-header__action site-header__search-trigger--mobile"
            aria-controls="site-search"
            aria-expanded={isSearchOpen}
            aria-label="Search"
            onClick={toggleSearch}
          >
            <HeaderIcon name="search" />
          </button>

          <Link
            className="site-header__action"
            to="/shop?wishlist=true"
            onClick={handleNavigation}
            aria-label={`Wishlist, ${wishlistCount} items`}
          >
            <span className="site-header__action-icon">
              <HeaderIcon name="heart" />
              {wishlistCount > 0 && (
                <span className="site-header__count" aria-hidden="true">
                  {wishlistCount > 99 ? "99+" : wishlistCount}
                </span>
              )}
            </span>
            <span className="site-header__action-label">Wishlist</span>
          </Link>

          <Link
            className="site-header__action"
            to="/cartpage"
            onClick={handleNavigation}
            aria-label={`Shopping bag, ${resolvedCartCount} items`}
          >
            <span className="site-header__action-icon">
              <HeaderIcon name="bag" />
              {resolvedCartCount > 0 && (
                <span className="site-header__count" aria-hidden="true">
                  {resolvedCartCount > 99 ? "99+" : resolvedCartCount}
                </span>
              )}
            </span>
            <span className="site-header__action-label">Bag</span>
          </Link>
        </div>
      </div>

      <nav className="site-header__navigation" aria-label="Primary navigation">
        {primaryNavigation.map((item) => (
          <Link
            className={item.accent ? "is-accent" : undefined}
            key={item.label}
            to={item.to}
            onClick={handleNavigation}
          >
            {item.label}
          </Link>
        ))}
        <details className="site-header__more">
          <summary>More</summary>
          <div>
            <Link to="/privacy" onClick={handleNavigation}>
              Privacy
            </Link>
            <Link to="/cookies" onClick={handleNavigation}>
              Cookies
            </Link>
            <Link to="/faqs" onClick={handleNavigation}>
              FAQs
            </Link>
            <Link to="/terms" onClick={handleNavigation}>
              Terms
            </Link>
          </div>
        </details>
      </nav>

      {isSearchOpen && (
        <div className="site-header__search-panel" id="site-search">
          <form
            className="site-header__search-form"
            role="search"
            onSubmit={handleSearch}
          >
            <HeaderIcon name="search" />
            <label className="sr-only" htmlFor="header-search-input">
              Search products
            </label>
            <input
              autoFocus
              id="header-search-input"
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search jackets, footwear and equipment"
            />
            <button type="submit">Search</button>
            <button
              type="button"
              className="site-header__search-close"
              aria-label="Close search"
              onClick={closeSearch}
            >
              <span aria-hidden="true">×</span>
            </button>
          </form>
        </div>
      )}

      {isMobileOpen && (
        <nav
          className="site-header__mobile-navigation"
          id="mobile-navigation"
          ref={mobileNavigationRef}
          aria-label="Mobile navigation"
        >
          <div className="site-header__mobile-primary">
            {primaryNavigation.map((item) => (
              <Link
                className={item.accent ? "is-accent" : undefined}
                key={item.label}
                to={item.to}
                onClick={handleNavigation}
              >
                <span>{item.label}</span>
                <span aria-hidden="true">→</span>
              </Link>
            ))}
            <Link to="/privacy" onClick={handleNavigation}>
              <span>Privacy</span>
              <span aria-hidden="true">→</span>
            </Link>
            <Link to="/cookies" onClick={handleNavigation}>
              <span>Cookies</span>
              <span aria-hidden="true">→</span>
            </Link>
            <Link to="/faqs" onClick={handleNavigation}>
              <span>FAQs</span>
              <span aria-hidden="true">→</span>
            </Link>
          </div>
          <div className="site-header__mobile-utility">
            {utilityNavigation.map((item) => (
              <Link key={item.label} to={item.to} onClick={handleNavigation}>
                {item.label}
              </Link>
            ))}
            <span>
              {regionLabel} · {siteConfig.currency || "USD"}
            </span>
          </div>
        </nav>
      )}
    </header>
  );
}

export default Header;
